import { useEffect } from 'react';

interface AlgorithmModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AlgorithmModal({ isOpen, onClose }: AlgorithmModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors cursor-pointer z-10"
        >
          <i className="ri-close-line text-xl text-gray-600"></i>
        </button>

        {/* Content */}
        <div className="p-6 md:p-10">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-block px-4 py-2 bg-green-100 text-green-700 text-sm font-semibold rounded-full mb-4 whitespace-nowrap">
              Truth Verification System
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Apple Truth Meter Calculator
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our proprietary algorithm analyzes news stories across multiple dimensions to provide an objective truth rating
            </p>
          </div>

          {/* Algorithm Components */}
          <div className="space-y-6 mb-10">
            <div className="bg-gradient-to-br from-green-50 to-white rounded-xl p-6 border-l-4 border-green-600">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                  <i className="ri-shield-check-line text-2xl text-white"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Source Credibility (30%)</h3>
                  <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                    Evaluates the reliability and track record of news sources, journalists, and publications
                  </p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                      <span>Historical accuracy of the source</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                      <span>Journalist credentials and reputation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                      <span>Publication editorial standards</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-white rounded-xl p-6 border-l-4 border-green-600">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                  <i className="ri-file-list-3-line text-2xl text-white"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Evidence Quality (25%)</h3>
                  <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                    Assesses the strength and verifiability of evidence supporting claims
                  </p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                      <span>Primary vs. secondary sources</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                      <span>Documentation and citations</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                      <span>Expert verification and peer review</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-white rounded-xl p-6 border-l-4 border-green-600">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                  <i className="ri-scales-3-line text-2xl text-white"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Cross-Reference Consistency (20%)</h3>
                  <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                    Compares reporting across multiple independent sources
                  </p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                      <span>Agreement across reputable outlets</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                      <span>Identification of discrepancies</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                      <span>Timeline and fact alignment</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-white rounded-xl p-6 border-l-4 border-green-600">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                  <i className="ri-eye-line text-2xl text-white"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Bias Detection (15%)</h3>
                  <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                    Identifies political, ideological, or commercial bias in reporting
                  </p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                      <span>Language sentiment analysis</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                      <span>Omission of key facts</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                      <span>Framing and context evaluation</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-white rounded-xl p-6 border-l-4 border-green-600">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                  <i className="ri-time-line text-2xl text-white"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Temporal Verification (10%)</h3>
                  <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                    Tracks how claims hold up over time and with new information
                  </p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                      <span>Updates and corrections tracking</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                      <span>Long-term accuracy assessment</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                      <span>Evolving evidence integration</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Scoring System */}
          <div className="bg-gray-900 rounded-2xl p-8 text-white mb-8">
            <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
              <i className="ri-calculator-line text-3xl"></i>
              Scoring System
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white/10 rounded-xl p-6">
                <div className="mb-3 w-14 h-14 flex items-center justify-center mx-auto">
                  <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M28 8C28 8 30 4 28 2C26 4 28 8 28 8Z" fill="#10B981"/>
                    <ellipse cx="28" cy="6" rx="2" ry="1" fill="#10B981"/>
                    <path d="M28 12C18 12 10 20 10 30C10 40 18 48 28 48C38 48 46 40 46 30C46 20 38 12 28 12Z" fill="#22C55E"/>
                    <path d="M28 12C18 12 10 20 10 30C10 40 18 48 28 48C38 48 46 40 46 30C46 20 38 12 28 12Z" fill="url(#greenGradient2)"/>
                    <ellipse cx="22" cy="24" rx="4" ry="5" fill="white" opacity="0.3"/>
                    <defs>
                      <linearGradient id="greenGradient2" x1="10" y1="12" x2="46" y2="48" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#22C55E"/>
                        <stop offset="1" stopColor="#16A34A"/>
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <h4 className="text-lg font-bold mb-2">Green Apple</h4>
                <p className="text-sm opacity-90 mb-2">Score: 70-100</p>
                <p className="text-xs opacity-75 leading-relaxed">High credibility, strong evidence, consistent reporting</p>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <div className="mb-3 w-14 h-14 flex items-center justify-center mx-auto">
                  <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M28 8C28 8 30 4 28 2C26 4 28 8 28 8Z" fill="#6B7280"/>
                    <ellipse cx="28" cy="6" rx="2" ry="1" fill="#6B7280"/>
                    <path d="M28 12C18 12 10 20 10 30C10 40 18 48 28 48C38 48 46 40 46 30C46 20 38 12 28 12Z" fill="#9CA3AF"/>
                    <path d="M28 12C18 12 10 20 10 30C10 40 18 48 28 48C38 48 46 40 46 30C46 20 38 12 28 12Z" fill="url(#greyGradient2)"/>
                    <ellipse cx="22" cy="24" rx="4" ry="5" fill="white" opacity="0.3"/>
                    <defs>
                      <linearGradient id="greyGradient2" x1="10" y1="12" x2="46" y2="48" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#9CA3AF"/>
                        <stop offset="1" stopColor="#6B7280"/>
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <h4 className="text-lg font-bold mb-2">Grey Apple</h4>
                <p className="text-sm opacity-90 mb-2">Score: 40-69</p>
                <p className="text-xs opacity-75 leading-relaxed">Mixed evidence, requires additional verification</p>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <div className="mb-3 w-14 h-14 flex items-center justify-center mx-auto">
                  <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M28 8C28 8 30 4 28 2C26 4 28 8 28 8Z" fill="#7C2D12"/>
                    <ellipse cx="28" cy="6" rx="2" ry="1" fill="#7C2D12"/>
                    <path d="M28 12C18 12 10 20 10 30C10 40 18 48 28 48C38 48 46 40 46 30C46 20 38 12 28 12Z" fill="#DC2626"/>
                    <path d="M28 12C18 12 10 20 10 30C10 40 18 48 28 48C38 48 46 40 46 30C46 20 38 12 28 12Z" fill="url(#redGradient2)"/>
                    <ellipse cx="22" cy="24" rx="4" ry="5" fill="white" opacity="0.3"/>
                    <defs>
                      <linearGradient id="redGradient2" x1="10" y1="12" x2="46" y2="48" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#EF4444"/>
                        <stop offset="1" stopColor="#DC2626"/>
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <h4 className="text-lg font-bold mb-2">Red Apple</h4>
                <p className="text-sm opacity-90 mb-2">Score: 0-39</p>
                <p className="text-xs opacity-75 leading-relaxed">Poor sourcing, potential misinformation</p>
              </div>
            </div>
          </div>

          {/* Methodology Note */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <i className="ri-information-line text-2xl text-green-600 flex-shrink-0 mt-1"></i>
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">Continuous Improvement</h4>
                <p className="text-sm text-gray-700 leading-relaxed">
                  The Apple Truth Meter Calculator is continuously refined based on feedback from our network of journalists, fact-checkers, and readers. We regularly update our methodology to adapt to evolving media landscapes and emerging verification techniques.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
